import { useAPIParticipateBahanAjarExperiment } from 'api/abtest'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import type { ToolkitBahanAjarDiscoveryExperiment } from 'types/toolkit'

import { getClientSideDeviceId } from 'utils/deviceId'
import { getExperimentUserData } from 'utils/experiment'
import { parseQuery } from 'utils/router'

import { useTogglesStore } from 'stores/toggles'

import { TOOLKIT_BAHAN_AJAR_DISCOVERY_EXPERIMENT_KEY } from 'data/toolkits'

const AB_TEST_WINNER = process.env
  .NEXT_PUBLIC_AB_TEST_WINNER as ToolkitBahanAjarDiscoveryExperiment

export const useAbTestBahanAjarDiscovery = () => {
  const deviceId = getClientSideDeviceId()
  const { query } = useRouter()
  const isAbTestToggleEnabled = useTogglesStore(
    (state) => state.toggles?.['ab-test-bahan-ajar-discovery']
  )

  const { mutateAsync: mutateExperimentParticipation } =
    useAPIParticipateBahanAjarExperiment()

  const alt1Query =
    parseQuery<string>(
      query?.IS_BAHAN_AJAR_DISCOVERY_ALT_1_ABTEST_QUERY_ENABLED
    ) || ''
  const alt2Query =
    parseQuery<string>(
      query?.IS_BAHAN_AJAR_DISCOVERY_ALT_2_ABTEST_QUERY_ENABLED
    ) || ''
  const controlQuery =
    parseQuery<string>(
      query?.IS_BAHAN_AJAR_DISCOVERY_CONTROL_ABTEST_QUERY_ENABLED
    ) || ''

  const isAlt1QueryEnabled = alt1Query === 'true'
  const isAlt2QueryEnabled = alt2Query === 'true'
  const isControlQueryEnabled = controlQuery === 'true'

  const [isLoading, setIsLoading] = useState(true)
  const [experimentStatus, setExperimentStatus] =
    useState<ToolkitBahanAjarDiscoveryExperiment>(AB_TEST_WINNER)

  const postExperiment = async () => {
    // Force variant to A/B Test winner since the experiment period is over
    if (!!AB_TEST_WINNER) {
      setExperimentStatus(AB_TEST_WINNER)
      setIsLoading(false)
      return
    }

    if (!isAbTestToggleEnabled) {
      setExperimentStatus('control')
      setIsLoading(false)
      return
    }

    // AB Test experiment status based on query
    if (isAlt1QueryEnabled) {
      setExperimentStatus('alt-1')
      setIsLoading(false)
      return
    }

    if (isAlt2QueryEnabled) {
      setExperimentStatus('alt-2')
      setIsLoading(false)
      return
    }

    if (isControlQueryEnabled) {
      setExperimentStatus('control')
      setIsLoading(false)
      return
    }

    try {
      const { userId } = await getExperimentUserData()

      // Participate to experiment
      const response = await mutateExperimentParticipation({
        experimentName: TOOLKIT_BAHAN_AJAR_DISCOVERY_EXPERIMENT_KEY,
        deviceId,
        userId: userId || '',
      })

      setExperimentStatus(
        response?.data?.experiment?.properties?.treatment || 'control'
      )
      setIsLoading(false)
    } catch {
      setExperimentStatus(AB_TEST_WINNER)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    postExperiment()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    isLoading,
    deviceId,
    experimentStatus,
  }
}
